// about.js — the "Studio Film Strip": pins the About section and maps the
// vertical scroll onto a horizontal track. Layered on top are a slow reverse
// parallax ghost title, per-image clip-path reveals + in-frame drift, a
// velocity-driven skew, staggered copy reveals, and a travel HUD.
//
// On mobile / reduced-motion the whole thing is skipped and CSS lays the
// panels out as a vertical stack (see the max-width: 900px block).
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const CHAPTERS = ['INTRO', 'WHO WE ARE', 'WHAT WE BELIEVE', 'HOW WE WORK', 'THE STUDIO'];

export function initAboutJourney() {
  const section = document.querySelector('[data-about]');
  const sticky  = document.querySelector('[data-about-sticky]');
  const track   = document.querySelector('[data-about-track]');
  if (!section || !sticky || !track) return;

  const mobile  = matchMedia('(max-width: 900px)').matches;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine    = matchMedia('(pointer: fine)').matches;

  // Reveal copy plainly and bail — CSS handles the vertical stack.
  if (mobile || reduced) {
    gsap.set('[data-about-fx]', { clearProps: 'all' });
    return;
  }

  const panels = [...track.children];
  const ghost  = section.querySelector('[data-about-ghost]');
  const bar     = section.querySelector('[data-about-bar]');
  const idxEl   = section.querySelector('[data-about-index]');
  const chapEl  = section.querySelector('[data-about-chapter]');

  // distance the track must travel so its right edge reaches the viewport edge
  const distance = () => Math.max(track.scrollWidth - window.innerWidth, 0);

  // ── main pin + horizontal scrub ──────────────────────────────────────
  const move = gsap.to(track, {
    x: () => -distance(),
    ease: 'none',
    scrollTrigger: {
      trigger: section,
      start: 'top top',
      end: () => '+=' + distance(),
      pin: true,
      anticipatePin: 1,
      scrub: 1,
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        // HUD progress + chapter, derived from which panel is centred
        if (bar) bar.style.width = (self.progress * 100).toFixed(1) + '%';
        const mid = -gsap.getProperty(track, 'x') + window.innerWidth / 2;
        let acc = parseFloat(getComputedStyle(track).paddingLeft) || 0;
        let active = 0;
        for (let i = 0; i < panels.length; i++) {
          acc += panels[i].offsetWidth;
          if (mid < acc) { active = i; break; }
          active = i;
        }
        if (idxEl) idxEl.textContent = String(active + 1).padStart(2, '0');
        if (chapEl && chapEl.textContent !== CHAPTERS[active]) {
          gsap.fromTo(chapEl, { opacity: 0, y: 6 }, { opacity: 1, y: 0, duration: 0.3, ease: 'power2.out' });
          chapEl.textContent = CHAPTERS[active];
        }
      },
    },
  });

  // ── ghost title: slow reverse parallax, tied to the same runway ──────
  if (ghost) {
    gsap.to(ghost, {
      x: () => -distance() * 0.4,
      ease: 'none',
      scrollTrigger: {
        trigger: section,
        start: 'top top',
        end: () => '+=' + distance(),
        scrub: true,
        invalidateOnRefresh: true,
      },
    });
  }

  // ── per-image reveals: clip-path wipe + settle, plus in-frame drift ──
  section.querySelectorAll('[data-about-img]').forEach((frame) => {
    const img = frame.querySelector('img');

    // start is held back so the frame is well inside the viewport before the
    // wipe begins; the window then runs past the left edge (negative end) so
    // the same scroll input is spread over twice the distance — a slow open.
    // power3.out front-loads it, so it still reads as open well before exit.
    gsap.fromTo(frame,
      { clipPath: 'inset(0 100% 0 0)' },
      {
        clipPath: 'inset(0 0% 0 0)',
        ease: 'power3.out',
        scrollTrigger: {
          trigger: frame,
          containerAnimation: move,
          start: 'left 82%',
          end: 'left -8%',
          scrub: true,
        },
      }
    );
    if (img) {
      // a deliberately deep push-in — the frame crops it, so the extra scale
      // reads as movement inside the image rather than a size change
      gsap.fromTo(img, { scale: 1.42 }, {
        scale: 1, ease: 'power2.out',
        scrollTrigger: { trigger: frame, containerAnimation: move, start: 'left 82%', end: 'left -17%', scrub: true },
      });
      // horizontal drift within the 116%-wide frame as it crosses the view
      gsap.fromTo(img, { xPercent: -6 }, {
        xPercent: 6, ease: 'none',
        scrollTrigger: { trigger: frame, containerAnimation: move, start: 'left right', end: 'right left', scrub: true },
      });
    }
  });

  // ── copy reveals: mask-up + fade, staggered per panel ────────────────
  const reveal = (fx) => gsap.to(fx, { opacity: 1, y: 0, duration: 0.9, stagger: 0.09, ease: 'power3.out' });
  panels.forEach((panel, i) => {
    const fx = panel.querySelectorAll('[data-about-fx]');
    if (!fx.length) return;
    gsap.set(fx, { opacity: 0, y: 40 });
    if (i === 0) {
      // panel 1 is on-screen the moment About arrives — a plain vertical
      // trigger fires reliably (and at the right time, as the section enters).
      ScrollTrigger.create({ trigger: section, start: 'top 78%', once: true, onEnter: () => reveal(fx) });
    } else {
      // later panels scroll in horizontally — drive them off the track motion
      ScrollTrigger.create({ trigger: panel, containerAnimation: move, start: 'left 72%', once: true, onEnter: () => reveal(fx) });
    }
  });

  // ── velocity-driven skew — its own trigger reads scroll velocity ─────
  if (fine) {
    const skewTo = gsap.quickTo(track, 'skewX', { duration: 0.6, ease: 'power3.out' });
    ScrollTrigger.create({
      trigger: section,
      start: 'top top',
      end: () => '+=' + distance(),
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        // velocity is px/s of the page scroll; map into a small skew, decay to 0
        const skew = gsap.utils.clamp(-3, 3, self.getVelocity() / -900);
        skewTo(skew);
      },
      onLeave:     () => skewTo(0),
      onLeaveBack: () => skewTo(0),
    });
    // idle decay — whenever scrolling settles, ease the skew back to flat
    let idle;
    section.addEventListener('wheel', () => {
      clearTimeout(idle);
      idle = setTimeout(() => skewTo(0), 120);
    }, { passive: true });
  }

  // keep runway length honest across resizes / late image loads
  window.addEventListener('load', () => ScrollTrigger.refresh());

  // dev handle (matches window.__stage / __scroll used elsewhere)
  window.__about = { move, ScrollTrigger, gsap, track, distance };
}
